import {
	Lesson,
	SortingOptions,
	Folder,
	ContentModule,
	FoldersAPIResponse,
	PortfoliosAPIResponse,
	LessonsAPIResponse,
    Portfolio,
} from '@DavisDigital/interfaces';

import { LessonService, PortfolioService, ContentService } from '@DavisDigital/davisdigitalfrontend.module';

class CopyContentModalState {
	public isLoading = false;
	
	public hasError = false;
	public hasSuccess = false;
	public message: string;
	
	public showConfirmDialog = false;
	public confirmMessage: string;
	
	public recordsPerPage = 8;

	public searchKeywords: string; // whatever visible in the UI
	public selectedSearchKeywords: string; // whatever is selected

	public readonly sortingOptions = SortingOptions;
	public sortOrder = 1; // ehatever is visible in the UI
	public selectedSortOrder = 1; // currently selected sort order

	public folders: Folder[] = undefined;
	public folder: Folder = undefined; // folder-dropdown-model
	public selectedFolder: Folder = undefined;
	
	public selectedLesson: Lesson;
	public activeLessons: Lesson[];
	public activeLessonsCurrentPage = 1;
	public activeLessonsPageCount: number;
	
	public selectedPortfolio;
	public activePortfolios;
	public activePortfoliosCurrentPage = 1;
	public activePortfoliosPageCount: number;

	public constructor() {}

	public setLoading = (isLoading: boolean) => {
		this.isLoading = isLoading;
	};
	
	public setError = (error=false, message ?: string) => {
		this.hasError = error;
	    this.message = message;
		if(this.hasError){
		    this.hasSuccess = false;
		}
	};
	
	public setSuccess = (success=false, message ?: string) => {
		this.hasSuccess = success;
	    this.message = message;
		if(this.hasSuccess){
		    this.hasError = false;
		}
	};
	
	public setShowConfirmDialog = (showConfirmDialog: boolean, confirmMessage ?: string) => {
		this.showConfirmDialog = showConfirmDialog;
		this.confirmMessage = confirmMessage;
	};

	public appllySearchKeywordsChange = () => {
		if (this.selectedSearchKeywords !== this.searchKeywords) {
			this.selectedSearchKeywords = this.searchKeywords;
			return true;
		}
		return false;
	};

	public applySortOrderChange() {
		if (this.selectedSortOrder !== this.sortOrder) {
			this.selectedSortOrder = this.sortOrder;
			return true;
		}
		return false;
	}

	public applyFolderChange() {
		if (this.selectedFolder !== this.folder) {
			this.selectedFolder = this.folder;
			return true;
		}
		return false;
	}

	public setFolders = (folders: Folder[]) => {
		this.folders = folders;
	};

	public setActiveLessons = (lessons: Lesson[], pageCount?: number) => {
		this.activeLessons = lessons;
		this.activeLessonsPageCount = pageCount || 1;
	};
	public setActiveLessonsCurrentPage = (pageNumber: number) => {
		this.activeLessonsCurrentPage = pageNumber;
	};
	
	public setActivePortfolios = (portfolios, pageCount?: number) => {
		this.activePortfolios = portfolios;
		this.activePortfoliosPageCount = pageCount || 1;
	};
	public setActivePortfoliosCurrentPage = (pageNumber: number) => {
		this.activePortfoliosCurrentPage = pageNumber;
	};
}

class CopyContentModalController {
	private close: Function; //injected from modal-service

	public lesson?: Lesson;
	public portfolio;
	public contentModule: ContentModule;

	public state$: CopyContentModalState;
	public displayType:string = "";
	
	// @ngInject
	constructor(
		private $log: ng.ILogService,
		private lessonService: LessonService,
		private contentService: ContentService,
		private portfolioService: PortfolioService,
	) {
		this.state$ = new CopyContentModalState();
	}

	public $onInit = () => {
			this.getFolders()
			.then(() => {
				if( this.lesson ){
					this.displayType = "Lesson";
					this.getLessons();
				} else if( this.portfolio) {
					this.displayType = "Portfolio";
					this.getPortfolios();
				}
			})
			.catch(this.$log.error);
		
	};

	public onSearch = () => {
		if (this.state$.appllySearchKeywordsChange()) {
			this.refresh();
		}
	};
	public onFolderChange = () => {
		if (this.state$.applyFolderChange()) {
			this.refresh();
		}
	};
	public onSortOrderChange = () => {
		if (this.state$.applySortOrderChange()) {
			this.refresh(true, true);
		}
	};
	public onLessonSelected = (lesson: Lesson) => {
		this.state$.selectedLesson = lesson;
	};
	
	public onPortfolioSelected = (portfolio:any) => {
		this.state$.selectedPortfolio = portfolio;
	};
	public onActiveLessonPaginationChange = (pageNumber: number) => {
		this.state$.setActiveLessonsCurrentPage(pageNumber);
		this.refresh(false);
	};
	public onConfirmCopyContent = () => {
		let confirmationText ="";
		if( this.lesson ) {
			confirmationText = `Copy content ${this.contentModule?.title} from Lesson ${this.lesson?.title} to Lesson ${this.state$.selectedLesson?.title || ''} ?`;
		} else {
			confirmationText = `Copy content ${this.contentModule?.title} from Portfolio ${this.portfolio.portfolio?.title} to Portfolio ${this.state$.selectedPortfolio?.title || ''} ?`;
		}
        this.state$.setShowConfirmDialog(
            true,
           confirmationText
        );
    }
    
    public onActionConfirmed = (confirm:boolean) => {
        this.state$.setShowConfirmDialog(false);
        if(confirm){
            this.copyContent();
        } 
    }
	
	public copyContent = () => {
	    this.state$.setLoading(true);
	    this.state$.setError(false);
	    
		this.contentService
		    .copyContentModule(this.contentModule, this.state$.selectedLesson, this.state$.selectedPortfolio)
		    .then((response) => {
		        if(!response?.data?.success){
		            throw(response);
		        } 
		        
		        let confirmationText ="";
				if( this.lesson ) {
					confirmationText = `Content ${this.contentModule.title} has been copied to Lesson "${this.state$.selectedLesson.title}" successfully.`;
				} else {
					confirmationText = `Content ${this.contentModule.title} has been copied to Portfolio "${this.state$.selectedPortfolio?.title}" successfully.`;
				}
		        this.state$.setSuccess(
		            true, 
		            confirmationText
		        );
		    })
		    .catch( e => {
		        this.$log.error(e);
		        this.state$.setError(true, e?.message || e?.data?.message || "Something went wrong...." ); 
		    })
		    .finally( () => this.state$.setLoading(false) )
	};

	private refresh = (resetCurrentPage = true, bustCache = false) => {
		if( this.lesson ){
		    this.getLessons(resetCurrentPage, bustCache);
		} 
		else if(this.portfolio ){
		    this.getPortfolios(resetCurrentPage, bustCache);
		}
	};

	private getFolders = (): ng.IPromise<any> => {
		this.state$.setLoading(true);

    	let onFoldersReceived = (response) => {
    		if (response?.data?.records) {
    			this.state$.setFolders(response.data.records);
    		} else {
    			throw response;
    		}
    	};
    	
		if( this.lesson ){
			return this.lessonService
        			.getMyLessonFolders({ showAll: true, ignoreArchiveFolder: true })
        			.then(onFoldersReceived);
		}
		else if( this.portfolio ){
		    return this.portfolioService
        		    .getMyPortfolioFolders({ showAll: true, ignoreArchiveFolder: true })
        		    .then(onFoldersReceived);
		}
		
	};

	private getLessons = (resetCurrentPage = true, bustCache = false): any => {
		this.state$.setLoading(true);

		if (resetCurrentPage) {
			this.state$.setActiveLessonsCurrentPage(1);
		}

		this.lessonService.getMyLessons({
				bustCache: bustCache,

				folder: this.state$.selectedFolder,
				includeFolderLessons: !this.state$.selectedFolder,
				
				keywords: this.state$.selectedSearchKeywords,
				sortOrder: this.state$.selectedSortOrder,
				currentPage: this.state$.activeLessonsCurrentPage,
				recordsPerPage: this.state$.recordsPerPage,
			})
			.then((response) => {
				this.state$.setLoading(false);
				if (response?.records) {
					this.state$.setActiveLessons(response.records, response.totalPages);
				} else {
					throw response;
				}
			})
			.catch(this.$log.error);
	};
	
	/**
	 * Method to get Portfolios
	 * */
	private getPortfolios = (resetCurrentPage = true, bustCache = false): any => {
		this.state$.setLoading(true);

		if (resetCurrentPage) {
			this.state$.setActivePortfoliosCurrentPage(1);
		}

		this.portfolioService.getMyPortfolios({
				bustCache: bustCache,

				folder: this.state$.selectedFolder,
				
				keywords: this.state$.selectedSearchKeywords,
				sortOrder: this.state$.selectedSortOrder,
				currentPage: this.state$.activePortfoliosCurrentPage,
				recordsPerPage: this.state$.recordsPerPage,
			})
			.then((response) => {
				this.state$.setLoading(false);
				if (response?.data?.records) {
					this.state$.setActivePortfolios(response.data.records, response.data.totalPages);
				} else {
					throw response;
				}
			})
			.catch(this.$log.error);
	};

	private closeModal = (whatever) => {
		this.close(whatever);
	};
}

class CopyContentModal {
	public restrict: string = 'E';
	public scope: {};

	public bindToController = {
		lesson: '<?',
		portfolio: '<?',
		contentModule: '<',
		close: '=',
	};

	public controller = CopyContentModalController;
	public controllerAs = 'copyContentModal';

	public templateUrl =
		__DAVIS_FRONTEND_BASE_PATH__ + '/davisdigital/directives/modals/copy-content-modal.html';

	public static Factory() {
		return () => new this();
	}
}

export { CopyContentModal };
